import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { doc, setDoc } from 'firebase/firestore';
import type React from 'react';
import { Fragment, useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { updateClientProfile } from '../../services/adminService';
import { resolveExternalSite } from '../../services/api';
import { indexStorage, scheduleDebouncedIndexStorage } from '../../services/contextHunterService';
import { firestore } from '../../services/firebase';
import storageService from '../../services/storageService';
import {
  ARCHETYPE_DESCRIPTIONS,
  ARCHETYPE_LABELS,
  type CollectionArchetype,
} from '../../types/knowledge';
import FolderBrowserModal from '../Admin/FolderBrowserModal';
import CopyButton from '../common/CopyButton';
import SharedDropzone from '../common/SharedDropzone';

interface DocumentUploaderProps {
  open: boolean;
  onClose: () => void;
  collectionType: CollectionArchetype;
  onUploadComplete: () => void;
}

const permissionRequestXml = `<AppPermissionRequests AllowAppOnlyPolicy="true">
  <AppPermissionRequest Scope="http://sharepoint/content/sitecollection/web" Right="Write" />
</AppPermissionRequests>`;

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  open,
  onClose,
  collectionType,
  onUploadComplete,
}) => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [view, setView] = useState<'selection' | 'upload' | 'sharepoint'>('selection');

  const [sharepointUrl, setSharepointUrl] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'failed'>('idle');
  const [destinationFolder, setDestinationFolder] = useState('');
  const [domain, setDomain] = useState<'tender' | 'grant' | 'global'>('global');
  const [isUploading, setIsUploading] = useState(false);

  const [browserOpen, setBrowserOpen] = useState(false);
  const [activeSite, setActiveSite] = useState<{
    siteId: string;
    driveId: string;
    url: string;
  } | null>(null);
  const [showNewUrlInput, setShowNewUrlInput] = useState(false);
  const [removedFolders, setRemovedFolders] = useState<string[]>([]);

  const connectedSites = useMemo(() => {
    const sites = new Map<
      string,
      { siteId: string; driveId: string; url: string; name: string; isPrimary: boolean }
    >();

    // Add primary site if exists
    if (account?.storageType === 'sharepoint' && account?.sharepointConfig?.siteUrl) {
      const siteId = (account.sharepointConfig.siteId ||
        account.sharepointConfig.defaultSiteId) as string;
      const driveId = (account.sharepointConfig.driveId ||
        account.sharepointConfig.defaultDriveId) as string;
      sites.set(siteId || 'primary', {
        siteId,
        driveId,
        url: account.sharepointConfig.siteUrl as string,
        name: 'The Default Site (Storage & Processing)',
        isPrimary: true,
      });
    }

    if (account?.llm_settings) {
      for (const value of Object.values(account.llm_settings)) {
        const setting = value as {
          external_site?: { siteId?: string; driveId?: string; siteUrl?: string };
        };
        if (
          setting?.external_site?.siteId &&
          setting?.external_site?.driveId &&
          setting?.external_site?.siteUrl
        ) {
          let name = setting.external_site.siteUrl;
          try {
            const urlObj = new URL(name);
            const match = urlObj.pathname.match(/^\/(sites|teams)\/([^/]+)/i);
            if (match?.[2]) {
              name = `${urlObj.hostname} - ${match[2]}`;
            } else {
              name = urlObj.hostname;
            }
          } catch {
            // ignore invalid URL
          }

          // If this site is already the primary site, just update its name or skip overriding its primary status
          const existing = sites.get(setting.external_site.siteId);
          sites.set(setting.external_site.siteId, {
            siteId: setting.external_site.siteId,
            driveId: setting.external_site.driveId,
            url: setting.external_site.siteUrl,
            name: existing?.isPrimary ? existing.name : name,
            isPrimary: existing?.isPrimary || false,
          });
        }
      }
    }
    return Array.from(sites.values());
  }, [account]);

  const handleFolderSelected = async (folderPath: string) => {
    if (!account?.uid || !account?.clientId || !activeSite) {
      return;
    }

    // Save the target path in llm_settings for this collection
    const newLlmSettings = JSON.parse(JSON.stringify(account.llm_settings || {}));
    newLlmSettings[collectionType] = newLlmSettings[collectionType] || {};
    newLlmSettings[collectionType].external_site = {
      siteId: activeSite.siteId,
      driveId: activeSite.driveId,
      siteUrl: activeSite.url,
    };

    const existingPaths = newLlmSettings[collectionType].discovery?.target_folder_paths || [];
    if (!existingPaths.includes(folderPath)) {
      existingPaths.push(folderPath);
    }

    newLlmSettings[collectionType].discovery = {
      target_folder_paths: existingPaths,
    };
    newLlmSettings[collectionType].automation = {
      frequency: 'daily',
      max_usd_limit: 10,
    };

    await updateClientProfile(account.clientId, { llm_settings: newLlmSettings });
    await refreshUserDetails(true);

    await indexStorage({
      userId: account.uid,
      clientId: account.clientId,
      folderPaths: [folderPath],
      siteId: activeSite.siteId,
      driveId: activeSite.driveId,
      mode: collectionType,
      collectionType,
    });

    showSnackbar(
      `Successfully queued folder "${folderPath || 'Root'}" for indexing in ${ARCHETYPE_LABELS[collectionType]}!`,
      'success'
    );
    onUploadComplete();
  };

  const handleFolderRemoved = async (folderPath: string) => {
    if (!account?.uid || !account?.clientId) {
      return;
    }

    setRemovedFolders((prev) => [...prev, folderPath]);

    const newLlmSettings = JSON.parse(JSON.stringify(account.llm_settings || {}));
    if (newLlmSettings[collectionType]?.discovery?.target_folder_paths) {
      newLlmSettings[collectionType].discovery.target_folder_paths = newLlmSettings[
        collectionType
      ].discovery.target_folder_paths.filter((p: string) => p !== folderPath);
    }

    await updateClientProfile(account.clientId, { llm_settings: newLlmSettings });
    // Use silent refresh to update context without causing a full page unmount
    await refreshUserDetails(true);

    showSnackbar(
      `Removed connection to "${folderPath || 'Root'}". Click "Sync Now" on the dashboard to apply changes.`,
      'success'
    );
  };

  const handleClose = () => {
    setView('selection');
    setSharepointUrl('');
    setConnectionStatus('idle');
    setDestinationFolder('');
    setShowNewUrlInput(false);
    setActiveSite(null);
    setBrowserOpen(false);
    onClose();
  };

  const getAppInvUrl = (urlStr: string) => {
    try {
      const url = new URL(urlStr);
      const match = url.pathname.match(/^\/(sites|teams)\/[^/]+/i);
      const sitePath = match ? match[0] : '';
      return `${url.origin}${sitePath}/_layouts/15/appinv.aspx`;
    } catch {
      return 'Invalid URL';
    }
  };

  const renderSelection = () => (
    <Stack spacing={3} sx={{ py: 2, alignItems: 'center' }}>
      <Box sx={{ width: '100%', mb: 3 }}>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
          How would you like to add documents to the{' '}
          <strong>{ARCHETYPE_LABELS[collectionType]}</strong> collection?
        </Typography>

        <Box
          sx={{
            bgcolor: 'background.default',
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mx: 'auto',
            maxWidth: '600px',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
            What belongs here:
          </Typography>
          <ul
            style={{
              margin: 0,
              paddingLeft: '24px',
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            <li style={{ marginBottom: '8px' }}>
              <strong>What it is:</strong> {ARCHETYPE_DESCRIPTIONS[collectionType]?.what}
            </li>
            <li>
              <strong>Usage:</strong> {ARCHETYPE_DESCRIPTIONS[collectionType]?.usage}
            </li>
          </ul>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'stretch' }}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<CloudUploadIcon sx={{ fontSize: 40 }} />}
          onClick={() => setView('upload')}
          sx={{
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            borderRadius: 2,
            flex: 1,
          }}
        >
          Upload Files
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'none' }}>
            Manually select and upload files from your computer.
          </Typography>
        </Button>

        <Tooltip
          title={
            account?.storageType !== 'sharepoint'
              ? 'Your organization is not configured to use SharePoint storage.'
              : ''
          }
          placement="bottom"
        >
          <Box sx={{ flex: 1, display: 'flex' }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<SyncIcon sx={{ fontSize: 40 }} />}
              onClick={() => setView('sharepoint')}
              disabled={account?.storageType !== 'sharepoint'}
              sx={{
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderRadius: 2,
                flex: 1,
              }}
            >
              Sync SharePoint Folder
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'none' }}>
                Connect to an existing SharePoint folder to index its contents automatically.
              </Typography>
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Stack>
  );

  const handleManualUpload = async (files: File[]) => {
    if (!account?.clientId) {
      return;
    }
    setIsUploading(true);

    try {
      for (const file of files) {
        // Clean and construct nested path
        const cleanedFolder = destinationFolder.replace(/^\/+|\/+$/g, '').trim();
        const folderPath = cleanedFolder ? `${cleanedFolder}/` : '';
        const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');

        // Define physical storage path
        const storagePath = `clients/${account.clientId}/knowledge_base/${collectionType}/${folderPath}${safeFileName}`;

        // 1. Upload file physically via storage abstraction (GCS or SharePoint)
        await storageService.uploadFile(storagePath, file);

        // Compute SHA-256 hash of the storage path to match the Python backend
        const msgBuffer = new TextEncoder().encode(storagePath);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const docId = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

        // 2. Save metadata to Knowledge Documents to trigger indexing
        const isSharepointStorage = account.storageType === 'sharepoint';
        const spSiteId = (account.sharepointConfig?.siteId ||
          account.sharepointConfig?.defaultSiteId) as string | undefined;
        const spDriveId = (account.sharepointConfig?.driveId ||
          account.sharepointConfig?.defaultDriveId) as string | undefined;

        const docRef = doc(firestore, 'clients', account.clientId, 'knowledge_documents', docId);
        await setDoc(
          docRef,
          {
            docId,
            fileName: file.name,
            sourceUrl: storagePath,
            storageType: isSharepointStorage ? 'sharepoint' : 'gcs',
            ...(Boolean(isSharepointStorage && spSiteId && spDriveId) && {
              sharepointMetadata: {
                siteId: spSiteId,
                driveId: spDriveId,
              },
            }),
            collectionType: collectionType,
            domain: domain === 'global' ? null : domain,
            syncStatus: 'pending',
            size: file.size,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      }

      if (account.uid) {
        const spSiteId = (account.sharepointConfig?.siteId ||
          account.sharepointConfig?.defaultSiteId) as string | undefined;
        const spDriveId = (account.sharepointConfig?.driveId ||
          account.sharepointConfig?.defaultDriveId) as string | undefined;
        scheduleDebouncedIndexStorage({
          userId: account.uid,
          clientId: account.clientId,
          folderPaths: [`clients/${account.clientId}/knowledge_base/${collectionType}`],
          ...(Boolean(account.storageType === 'sharepoint' && spSiteId && spDriveId) && {
            siteId: spSiteId as string,
            driveId: spDriveId as string,
          }),
          mode: collectionType,
          collectionType,
        });
      }

      showSnackbar(
        `Successfully uploaded ${files.length} file(s) to ${ARCHETYPE_LABELS[collectionType]}.`,
        'success'
      );
      onUploadComplete();
      handleClose();
    } catch (e: unknown) {
      const err = e as { message?: string };
      showSnackbar(err.message || 'Failed to upload files.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const renderUpload = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Upload files to the <strong>{ARCHETYPE_LABELS[collectionType]}</strong> collection. Files
        will be securely stored in{' '}
        {account?.storageType === 'sharepoint'
          ? 'your connected SharePoint drive'
          : 'Google Cloud Storage'}
        .
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
          Which domain should the file(s) be saved for?
        </Typography>
        <ToggleButtonGroup
          value={domain}
          exclusive
          onChange={(_e, newDomain) => {
            if (newDomain !== null) {
              setDomain(newDomain as typeof domain);
            }
          }}
          size="small"
          color="primary"
          sx={{ width: '100%' }}
        >
          <ToggleButton value="tender" sx={{ flex: 1 }}>
            Tenders
          </ToggleButton>
          <ToggleButton value="grant" sx={{ flex: 1 }}>
            Grants
          </ToggleButton>
          <ToggleButton value="global" sx={{ flex: 1 }}>
            Both (Global)
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>

      <TextField
        fullWidth
        label="Destination Folder (Optional)"
        placeholder="e.g. engineering"
        value={destinationFolder}
        onChange={(e) => setDestinationFolder(e.target.value)}
        sx={{ mb: 3 }}
        helperText={`Enter a folder name to create a folder for these files within ${ARCHETYPE_LABELS[collectionType]}.`}
        disabled={isUploading}
      />

      <SharedDropzone type="tender_docs" onFilesSelected={handleManualUpload} />

      {isUploading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
          <CircularProgress size={24} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Uploading and organizing files...
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
        <Button onClick={() => setView('selection')}>Back</Button>
      </Box>
    </Box>
  );

  const handleTestConnection = async () => {
    if (!account?.uid || !account?.clientId) {
      return;
    }
    if (!sharepointUrl) {
      showSnackbar('Please enter a valid SharePoint folder URL.', 'warning');
      return;
    }

    setConnectionStatus('testing');
    try {
      const res = (await resolveExternalSite({
        userId: account.uid,
        clientId: account.clientId,
        siteUrl: sharepointUrl,
      })) as { success: boolean; error?: string; siteId?: string; driveId?: string };

      if (res.success && res.siteId && res.driveId) {
        setConnectionStatus('idle');
        setActiveSite({ siteId: res.siteId, driveId: res.driveId, url: sharepointUrl });
        setBrowserOpen(true);
      } else {
        setConnectionStatus('failed');
      }
    } catch (_e: unknown) {
      setConnectionStatus('failed');
    }
  };

  const renderSharePoint = () => {
    const currentSettings = (account?.llm_settings as Record<string, unknown>)?.[
      collectionType
    ] as {
      discovery?: { target_folder_paths?: string[] };
      external_site?: { siteId?: string };
    };
    const existingPaths = (currentSettings?.discovery?.target_folder_paths || []).filter(
      (p: string) => !removedFolders.includes(p)
    );
    const activeSiteId = currentSettings?.external_site?.siteId;

    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Select a connected SharePoint site or add a new one to sync into the{' '}
          <strong>{ARCHETYPE_LABELS[collectionType]}</strong> collection.
        </Typography>

        {connectedSites.length > 0 && !showNewUrlInput && (
          <Box sx={{ mb: 3 }}>
            {connectedSites.some((s) => s.isPrimary) && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  The Default Site
                </Typography>
                <List
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 3,
                  }}
                >
                  {connectedSites
                    .filter((s) => s.isPrimary)
                    .map((site, index, arr) => {
                      const isCurrentSite = site.siteId === activeSiteId;
                      const sitePaths = isCurrentSite ? existingPaths : [];

                      return (
                        <Fragment key={site.siteId}>
                          <ListItem
                            disablePadding
                            divider={index < arr.length - 1 || sitePaths.length > 0}
                          >
                            <ListItemButton
                              onClick={() => {
                                setActiveSite(site);
                                setBrowserOpen(true);
                              }}
                              sx={{ py: 2 }}
                            >
                              <ListItemIcon>
                                <FolderSharedIcon color={isCurrentSite ? 'primary' : 'action'} />
                              </ListItemIcon>
                              <ListItemText
                                primary={site.name}
                                secondary={site.url}
                                sx={{ wordBreak: 'break-all' }}
                                primaryTypographyProps={{ fontWeight: isCurrentSite ? 600 : 400 }}
                              />
                              <Button variant="outlined" size="small">
                                Browse
                              </Button>
                            </ListItemButton>
                          </ListItem>

                          {sitePaths.length > 0 && (
                            <Collapse in={true} timeout="auto" unmountOnExit>
                              <List
                                component="div"
                                disablePadding
                                sx={{
                                  bgcolor: 'background.default',
                                  position: 'relative',
                                  ml: 3,
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 16,
                                    left: 20,
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                  },
                                }}
                              >
                                {sitePaths.map((path: string) => (
                                  <ListItem
                                    key={path}
                                    sx={{
                                      pl: 6,
                                      position: 'relative',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: 20,
                                        width: 16,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                      },
                                    }}
                                    divider
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setActiveSite(site);
                                          await handleFolderRemoved(path);
                                        }}
                                        color="error"
                                        size="small"
                                      >
                                        <DeleteIcon sx={{ fontSize: 28 }} />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <FolderSpecialIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={path || 'Root Folder'}
                                      secondaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                          )}
                        </Fragment>
                      );
                    })}
                </List>
              </>
            )}

            {connectedSites.some((s) => !s.isPrimary) && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  Knowledge Base Connections
                </Typography>
                <List
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  {connectedSites
                    .filter((s) => !s.isPrimary)
                    .map((site, index, arr) => {
                      const isCurrentSite = site.siteId === activeSiteId;
                      const sitePaths = isCurrentSite ? existingPaths : [];

                      return (
                        <Fragment key={site.siteId}>
                          <ListItem
                            disablePadding
                            divider={index < arr.length - 1 || sitePaths.length > 0}
                          >
                            <ListItemButton
                              onClick={() => {
                                setActiveSite(site);
                                setBrowserOpen(true);
                              }}
                              sx={{ py: 2 }}
                            >
                              <ListItemIcon>
                                <FolderSharedIcon color={isCurrentSite ? 'primary' : 'action'} />
                              </ListItemIcon>
                              <ListItemText
                                primary={site.name}
                                secondary={site.url}
                                sx={{ wordBreak: 'break-all' }}
                                primaryTypographyProps={{ fontWeight: isCurrentSite ? 600 : 400 }}
                              />
                              <Button variant="outlined" size="small">
                                Browse
                              </Button>
                            </ListItemButton>
                          </ListItem>

                          {sitePaths.length > 0 && (
                            <Collapse in={true} timeout="auto" unmountOnExit>
                              <List
                                component="div"
                                disablePadding
                                sx={{
                                  bgcolor: 'background.default',
                                  position: 'relative',
                                  ml: 3,
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 16,
                                    left: 20,
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                  },
                                }}
                              >
                                {sitePaths.map((path: string) => (
                                  <ListItem
                                    key={path}
                                    sx={{
                                      pl: 6,
                                      position: 'relative',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: 20,
                                        width: 16,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                      },
                                    }}
                                    divider
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setActiveSite(site);
                                          await handleFolderRemoved(path);
                                        }}
                                        color="error"
                                        size="small"
                                      >
                                        <DeleteIcon sx={{ fontSize: 28 }} />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <FolderSpecialIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={path || 'Root Folder'}
                                      secondaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                          )}
                        </Fragment>
                      );
                    })}
                </List>
              </>
            )}

            <Button variant="outlined" onClick={() => setShowNewUrlInput(true)}>
              Connect Another Site
            </Button>
          </Box>
        )}

        {(connectedSites.length === 0 || showNewUrlInput) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Connect a New Site
            </Typography>
            <TextField
              fullWidth
              label="SharePoint Folder or Site URL"
              placeholder="https://acme.sharepoint.com/sites/LegalDept/Shared Documents"
              value={sharepointUrl}
              onChange={(e) => {
                setSharepointUrl(e.target.value);
                setConnectionStatus('idle');
              }}
              sx={{ mb: 3 }}
            />
            {connectedSites.length > 0 && (
              <Button
                sx={{ mr: 2 }}
                onClick={() => {
                  setShowNewUrlInput(false);
                  setSharepointUrl('');
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}

        {connectionStatus === 'failed' && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Access Denied (403 Forbidden)
              </Typography>
              <Typography variant="body2">
                Bidlogiq does not have permission to read this specific SharePoint site. Because of
                strict enterprise security (Sites.Selected), your IT Admin must explicitly grant
                access to this site.
              </Typography>
            </Alert>

            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Step 1: Go to the App Permissions Page for this site
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
                  p: 1,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', flexGrow: 1, wordBreak: 'break-all' }}
                >
                  {getAppInvUrl(sharepointUrl)}
                </Typography>
                <CopyButton textToCopy={getAppInvUrl(sharepointUrl)} />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Step 2: Enter your Client ID and click "Lookup"
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
                  p: 1,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                  {String(account?.sharepointConfig?.clientId || 'Available in Admin Panel')}
                </Typography>
                <CopyButton textToCopy={String(account?.sharepointConfig?.clientId || '')} />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Step 3: Paste this exact XML into the "Permission Request XML" box and click
                "Create"
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
                  p: 1,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Box component="pre" sx={{ m: 0, overflowX: 'auto', fontSize: '0.8rem' }}>
                  {permissionRequestXml}
                </Box>
                <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton textToCopy={permissionRequestXml} />
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Step 4: Click "Trust It"
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Once you click "Trust It", return here and try the connection again.
              </Typography>

              <Button variant="outlined" color="primary" onClick={handleTestConnection}>
                I've granted access, try again
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center' }}>
          <Button onClick={() => setView('selection')}>Back</Button>

          {connectionStatus === 'idle' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestConnection}
              disabled={!sharepointUrl}
            >
              Connect & Sync Folder
            </Button>
          )}

          {connectionStatus === 'testing' && (
            <Button variant="contained" disabled startIcon={<CircularProgress size={20} />}>
              Testing Connection...
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>
            {view === 'selection' && `Add to ${ARCHETYPE_LABELS[collectionType]}`}
            {view === 'upload' && 'Upload Files'}
            {view === 'sharepoint' && 'Sync SharePoint Folder'}
          </span>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {view === 'selection' && renderSelection()}
          {view === 'upload' && renderUpload()}
          {view === 'sharepoint' && renderSharePoint()}
        </DialogContent>
      </Dialog>
      {activeSite && (
        <FolderBrowserModal
          open={browserOpen}
          onClose={() => setBrowserOpen(false)}
          onSelect={handleFolderSelected}
          onRemovePath={handleFolderRemoved}
          siteId={activeSite.siteId}
          driveId={activeSite.driveId}
          mode={collectionType}
          existingPaths={
            (
              (account?.llm_settings as Record<string, unknown>)?.[collectionType] as {
                discovery?: { target_folder_paths?: string[] };
              }
            )?.discovery?.target_folder_paths || []
          }
        />
      )}
    </>
  );
};

export default DocumentUploader;
